import * as React from "react";
import { IStore } from "reducers";
import { connect } from "react-redux";
import { Attribute } from "reducers/dimensions-panel/dimensions-panel";
import { ListDraggableItem } from "components/common/list-draggable-item";
import { moveAttribute, selectAllValues } from "actions/tool-panel/draft/actions";
import { DimensionPanelDefaultAction, MoveAttribute } from "actions/tool-panel/draft/types";
import { Card } from "./attributes-card";
import { SelectedDimension, useDimensionPanelContext } from "../../dimensions-tool-panel";
import { DoorAttributesToolbar } from "./door-attributes-toolbar";
import CreateDoorAttributesDialogContainer from "./create-door-attributes-dialog-container";
import { useVirtualListCache, VirtualCachedRowList } from "components/common/virtual-list";
import { IMerchantSharedSettings, MAX_USER_CUSTOM_ATTRIBUTES } from "constants/merchant";

const COLUMN_INDEX = 0;
const APPLY_BUTTON_CONTAINER_HEIGHT = 50;

type Props = {
    setSelectedDimension: React.Dispatch<React.SetStateAction<SelectedDimension>>;
    onImportAttributesToggle: () => void;
};

type ReduxProps = {
    attributes: Attribute[];
    collectionsRefs: { [collectionId: string]: string };
    moveAttributeAction: MoveAttribute;
    selectAllValuesAction: DimensionPanelDefaultAction;
    merchantSharedSettings: IMerchantSharedSettings;
};

export const AttributesTab: React.FC<Props & ReduxProps> = ({
    attributes,
    collectionsRefs,
    moveAttributeAction,
    selectAllValuesAction,
    setSelectedDimension,
    onImportAttributesToggle,
    merchantSharedSettings,
}) => {
    const [attributeKey, setAttributeKey] = React.useState<string | undefined>(undefined);
    const [isConfirmDialogOpen, setConfirmDialogOpen] = React.useState(undefined);
    const [isDialogOpen, setDialogOpen] = React.useState(undefined);

    const { custom_attributes_limit } = merchantSharedSettings;
    const customAttributesLimit = custom_attributes_limit || MAX_USER_CUSTOM_ATTRIBUTES;

    const { editable, isRollupMode, onEditDoorGroupingRequest } = useDimensionPanelContext();

    const cardClickHandler = React.useCallback(
        (id: string, label: string, index) => {
            setSelectedDimension({
                id,
                label,
                kind: "attributes",
                index,
            });
        },
        [setSelectedDimension]
    );

    const handleAddDoorGrouping = () => {
        setAttributeKey(null);
        setDialogOpen(true);
    };

    const onEditAttributeHandler = React.useCallback((id: string) => {
        setAttributeKey(id);
        setDialogOpen(true);
    }, []);

    const onDeleteAttributeHandler = React.useCallback((id: string) => {
        setConfirmDialogOpen(true);
        setAttributeKey(id);
    }, []);

    const virtualListCache = useVirtualListCache({
        cacheOptions: {
            defaultHeight: 40,
            fixedWidth: true,
            minHeight: 40,
        },
    });
    const { cache, recomputeRowHeight, clearRowCache } = virtualListCache;

    const handleDragRow = (dragIndex: number, hoverIndex: number) => {
        moveAttributeAction({ dragIndex, hoverIndex });
        clearRowCache(dragIndex);
        clearRowCache(hoverIndex);
        recomputeRowHeight(dragIndex);
        recomputeRowHeight(hoverIndex);
    };

    const handleToggleSelect = (index: number) => {
        selectAllValuesAction({ dimensionIndex: index, selectionKind: "attributes" });
        cache.clear(index, COLUMN_INDEX);
    };

    return (
        <>
            <DoorAttributesToolbar
                attributes={attributes}
                editable={editable}
                handleAddDoorGrouping={handleAddDoorGrouping}
                handleImportAttributesToggle={onImportAttributesToggle}
                handleEditDoorGrouping={onEditDoorGroupingRequest}
                customAttributesLimit={customAttributesLimit}
            />
            {/* Dialogs - YES! this single component handle both the confirmation and create attribute dialogs */}
            {(isDialogOpen || isConfirmDialogOpen) && (
                <CreateDoorAttributesDialogContainer
                    isDialogOpen={isDialogOpen}
                    isConfirmDialogOpen={isConfirmDialogOpen}
                    onCancel={() => setDialogOpen(false)}
                    onCancelConfirmDialog={() => setConfirmDialogOpen(false)}
                    attributeKey={attributeKey}
                    onEditDoorGroupingRequest={onEditDoorGroupingRequest}
                />
            )}
            <VirtualCachedRowList
                data={attributes}
                hookInstance={virtualListCache}
                customWrapperCSS={{ height: `calc(100% - ${APPLY_BUTTON_CONTAINER_HEIGHT}px)` }}
            >
                {({ element, renderProps }) => {
                    const { index } = renderProps;
                    return (
                        <ListDraggableItem
                            key={element.id}
                            id={element.id}
                            index={index}
                            onMove={(dragIndex: number, hoverIndex: number) => handleDragRow(dragIndex, hoverIndex)}
                        >
                            {(props) => (
                                <Card
                                    key={index}
                                    label={element.name}
                                    sublabel={element.collectionId ? collectionsRefs[element.collectionId] : undefined}
                                    id={element.id}
                                    isDragging={false}
                                    isRollupMode={isRollupMode}
                                    onEditAttributeHandler={() => onEditAttributeHandler(element.id)}
                                    onDeleteAttributeHandler={() => onDeleteAttributeHandler(element.id)}
                                    selected={element.selected}
                                    selectDimensionHandler={() => cardClickHandler(element.id, element.name, index)}
                                    isUserDefined={element.isUserDefined}
                                    onToggleSelect={() => handleToggleSelect(index)}
                                    optionsCount={element.values.length}
                                    selectedOptionsCount={element.values.filter((value) => value.selected).length}
                                    {...props}
                                />
                            )}
                        </ListDraggableItem>
                    );
                }}
            </VirtualCachedRowList>
        </>
    );
};

export const AttributesTabContainer = connect<{}, {}, Props>(
    (state: IStore) => ({
        attributes: state.dimensionsPanel.draft.attributes,
        collectionsRefs: state.collection.collectionsRefs,
        merchantSharedSettings: state.merchantDefaultSharedSettings.result ?? {},
    }),
    {
        selectAllValuesAction: selectAllValues,
        moveAttributeAction: moveAttribute,
    }
)(AttributesTab);
